import { useContext, useEffect, useState } from "react";
import { useMutation, useQueryClient } from "react-query";
import { toast } from "react-toastify";
import { AuthContext } from "../../../Context/AuthContext";
import StexSelect from "../../../Routes/Marketplace/components/StexSelect";
import "../../../Routes/Marketplace/components/StexSelect.css";
import {
  MOBILE_MONEY_PROVIDERS,
  buildMobileMoneyPayload,
} from "../utils/merchantBusinessFormConfig";

const PROVIDER_OPTIONS = MOBILE_MONEY_PROVIDERS.map((item) => ({
  value: item,
  label: item,
}));

const MerchantMobileMoneySetupForm = ({ merchant, onSuccess, onCancel }) => {
  const { user, token } = useContext(AuthContext);
  const queryClient = useQueryClient();
  const [provider, setProvider] = useState(merchant?.mobileMoneyProvider || "");
  const [number, setNumber] = useState(merchant?.mobileMoneyNumber || "");

  useEffect(() => {
    setProvider(merchant?.mobileMoneyProvider || "");
    setNumber(merchant?.mobileMoneyNumber || "");
  }, [merchant]);

  const canSubmit = Boolean(provider) && Boolean(number.trim());

  const mutation = useMutation(
    async () => {
      const response = await fetch(
        `${process.env.REACT_APP_SERVER_URL}/users/update/merchants/${user.userId}`,
        {
          method: "PATCH",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify(buildMobileMoneyPayload(provider, number)),
        }
      );
      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        throw new Error(err.message || "Could not save mobile money details");
      }
      return response.json();
    },
    {
      onSuccess: (data) => {
        toast.success("Mobile money payout saved.");
        queryClient.invalidateQueries("businessMerchant");
        onSuccess?.(data.merchant);
      },
      onError: (error) => {
        toast.error(error.message || "Could not save mobile money details");
      },
    }
  );

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        if (!canSubmit) {
          toast.error("Select a provider and enter your mobile money number.");
          return;
        }
        mutation.mutate();
      }}
      className="dashboard-form-grid dashboard-payout-form-grid"
    >
      <p className="dashboard-form-hint dashboard-form-hint--flush dashboard-form-field--full">
        Choose your provider and payout number. You can change this anytime before requesting a withdrawal.
      </p>

      <div className="dashboard-form-field dashboard-form-field--select">
        <StexSelect
          name="mobileMoneyProvider"
          label="Provider *"
          value={provider}
          onChange={(e) => setProvider(e.target.value)}
          options={PROVIDER_OPTIONS}
          placeholder="Select provider"
          required
        />
      </div>
      <label className="dashboard-form-field">
        <span>Mobile money number *</span>
        <input type="tel" value={number} onChange={(e) => setNumber(e.target.value)} required />
      </label>
      <div className="dashboard-form-field dashboard-form-field--full dashboard-payout-form-actions">
        {onCancel && (
          <button type="button" className="dashboard-btn dashboard-btn--secondary" onClick={onCancel}>
            Cancel
          </button>
        )}
        <button
          type="submit"
          className="dashboard-btn dashboard-btn--primary"
          disabled={mutation.isLoading || !canSubmit}
        >
          {mutation.isLoading ? "Saving…" : "Save payout method"}
        </button>
      </div>
    </form>
  );
};

export default MerchantMobileMoneySetupForm;
